/**
 * ============================================================================
 * NOTIFICATION BELL COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Notification bell icon with badge and dropdown
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNotificationStore } from '../../store';
import NotificationBadge from './NotificationBadge';
import NotificationCenter from './NotificationCenter';
import Dropdown from '../common/Dropdown';

/**
 * Notification Bell Component
 * @param {Object} props
 * @param {string} props.size - Size (sm, md, lg)
 * @param {string} props.variant - Variant (default, filled, outline)
 * @param {Function} props.onClick - Click handler
 * @returns {JSX.Element}
 */
const NotificationBell = ({ size = 'md', variant = 'default', onClick }) => {
  const { unreadCount } = useNotificationStore();
  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const variantClasses = {
    default: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
    filled: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
    outline: 'border-2 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600',
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative" ref={bellRef}>
      <button
        onClick={handleClick}
        className={`
          relative
          ${sizeClasses[size] || sizeClasses.md}
          ${variantClasses[variant] || variantClasses.default}
          rounded-xl
          flex items-center justify-center
          transition-all duration-200
          transform hover:scale-110 active:scale-95
          focus:outline-none focus:ring-4 focus:ring-blue-500/20
        `}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        {/* Bell Icon */}
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge */}
        <NotificationBadge count={unreadCount} variant="pulse" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 z-50 animate-scaleIn">
          <NotificationCenter onClose={() => setIsOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
